import React from 'react';
import { X, Copy, Check } from 'lucide-react';
import { Book } from '@/types/library';

interface CiteModalProps {
  book: Book;
  isOpen: boolean;
  onClose: () => void;
}

const CiteModal: React.FC<CiteModalProps> = ({ book, isOpen, onClose }) => {
  const [copiedFormat, setCopiedFormat] = React.useState<string | null>(null);

  if (!isOpen) return null;

  const generateAPACitation = () => {
    const authors = book.authors.join(', ');
    const year = book.year || 'n.d.';
    return `${authors}. (${year}). ${book.title}${book.edition ? ` (${book.edition} ed.)` : ''}. ${book.publisher || 'Publisher unknown'}.`;
  };

  const generateChicagoCitation = () => {
    const authors = book.authors.join(', ');
    return `${authors}. ${book.title}${book.edition ? `, ${book.edition} ed.` : ''}. ${book.publisher || 'Publisher unknown'}, ${book.year || 'n.d.'}.`;
  };

  const copyToClipboard = (text: string, format: string) => {
    navigator.clipboard.writeText(text);
    setCopiedFormat(format);
    setTimeout(() => setCopiedFormat(null), 2000);
  };

  const apaCitation = generateAPACitation();
  const chicagoCitation = generateChicagoCitation();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cite-modal-title"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 id="cite-modal-title" className="text-xl font-semibold text-gray-900">
              Cite this Book
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 rounded p-1"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* APA Format */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-gray-900">APA Format</h3>
                <button
                  onClick={() => copyToClipboard(apaCitation, 'apa')}
                  className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
                >
                  {copiedFormat === 'apa' ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {apaCitation}
                </p>
              </div>
            </div>

            {/* Chicago Format */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-gray-900">Chicago Format</h3>
                <button
                  onClick={() => copyToClipboard(chicagoCitation, 'chicago')}
                  className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
                >
                  {copiedFormat === 'chicago' ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {chicagoCitation}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CiteModal;